import type { ReactElement } from 'react';
import { useShortcutRevealed } from '../keymap/shortcutReveal';

export interface ShortcutBadgeProps {
  /**
   * The formatted shortcut label (e.g. `⌘S`, `Ctrl+\`). Produced by the
   * `shortcutLabel`/`viewSwitchLabel` helpers. Empty/absent → nothing renders.
   */
  label: string;
  /** Extra class for host-specific positioning tweaks. */
  className?: string;
}

/**
 * A small keyboard-shortcut badge that floats in the TOP-RIGHT corner of its
 * host control, visible only while shortcut-reveal mode is active (the platform
 * "command" modifier held alone for ~1s — see {@link installShortcutReveal}).
 *
 * `aria-hidden` is intentional: the real, persistent shortcut already lives on
 * the host control's `title` / `aria-keyshortcuts`, so screen-reader and
 * tooltip users are unaffected by this purely-visual affordance. The host must
 * be a positioning context (`position: relative`) for the badge to anchor.
 */
export function ShortcutBadge({ label, className }: ShortcutBadgeProps): ReactElement | null {
  const revealed = useShortcutRevealed();
  if (!revealed || !label) return null;
  return (
    <span className={['mp-kbd-badge', className].filter(Boolean).join(' ')} aria-hidden>
      {label}
    </span>
  );
}
